import { userServiceApiSlice } from "state/services/userServiceApiSlice";
import { adRequestStatusMap } from "utils/constants";
import { generateRandomColor } from "utils/generateRandomColor";
import { moveOrgArrayElement } from "utils/moveOrgArrayElement";

// The `userServiceApiQueries` object is used to generate hooks that can be used to make API calls to the user service.
export const userServiceApiQueries = userServiceApiSlice.injectEndpoints({
  // The `endpoints` object is used to define the API endpoints for the user service.
  endpoints: (builder) => ({
    // The `getUser` query is used to retrieve a user entry by email.
    getUser: builder.query({
      // Accepts email and optional platformId
      query: ({ email, platformId }) => {
        let url = `/users?email=${encodeURIComponent(email)}`;
        if (platformId) {
          url += `&platform_id=${encodeURIComponent(platformId)}`;
        }
        return { url };
      },
      transformResponse: (responseData) => {
        return responseData.users;
      },
      providesTags: [{ type: "User", id: "Entry" }],
    }),
    // The `getUserByEmail` query fetches a user by email only (no platform filter), returning data across all platforms.
    getUserByEmail: builder.query({
      query: ({ email }) => ({
        url: `/users?email=${encodeURIComponent(email)}`,
      }),
      transformResponse: (responseData) => {
        return responseData.users;
      },
      providesTags: [{ type: "User", id: "EntryByEmail" }],
    }),
    // The `getUserWithOrgMembershipStatuses` query is used to retrieve a user with their organization membership statuses.
    getUserWithOrgMembershipStatuses: builder.query({
      query: ({ userId, platformId }) => ({
        url: `/users/${userId}/organization/membership?platform_id=${platformId}`,
      }),
      transformResponse: (responseData, meta, { userId, platformId }) => {
        // The `orgsListFormatted` array is used to store the formatted organization membership statuses for the user.
        try {
          const email = responseData?.email;
          const orgsListWithUserStatus =
            responseData?.platforms?.[platformId]?.organizations;

          // Split orgs into two arrays: joined/recommended and non-recommended
          let orgs = [{}, {}];

          // Add orgs with "Joined" status to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (orgsListWithUserStatus[orgId].membership_status === "Joined") {
              orgs[0][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Add recommended orgs to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (
              orgsListWithUserStatus[orgId].recommended === true &&
              orgsListWithUserStatus[orgId].membership_status !== "Joined"
            ) {
              orgs[0][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Add non-recommended orgs to orgs object
          for (const orgId in orgsListWithUserStatus) {
            if (
              orgsListWithUserStatus[orgId].recommended === false &&
              orgsListWithUserStatus[orgId].membership_status !== "Joined"
            ) {
              orgs[1][orgId] = orgsListWithUserStatus[orgId];
            }
          }

          // Format orgs object into orgsListFormatted array, maintaining split between joined/recommended and non-recommended orgs
          const orgsListFormatted = [[], []];
          let i = 1;

          for (const orgId in orgs[0]) {
            if (
              orgsListWithUserStatus[orgId].visible === true || // Modification
              orgsListWithUserStatus[orgId].user_type === "Owner" || // Modification
              orgsListWithUserStatus[orgId].user_type === "Approver" // Modification
            ) {
              orgsListFormatted[0].push({
                s_no: i++,
                id: orgId,
                orgDisplayName: orgsListWithUserStatus[orgId].org_name.replace(
                  /-/g,
                  " "
                ),
                orgName: orgsListWithUserStatus[orgId].org_name,
                status:
                  adRequestStatusMap[
                  orgsListWithUserStatus[orgId].membership_status
                  ],
                action:
                  adRequestStatusMap[
                    orgsListWithUserStatus[orgId].membership_status
                  ] === "Non-Member"
                    ? "Add"
                    : adRequestStatusMap[
                      orgsListWithUserStatus[orgId].membership_status
                    ] === "Failed"
                      ? "Add"
                      : "Delete",
                email: email,
                approvalRequired:
                  orgsListWithUserStatus[orgId]?.approval_required === true
                    ? "Yes"
                    : "No",
                userType: orgsListWithUserStatus[orgId]?.user_type,
                //userType: "Owner",
                signup_banner: orgsListWithUserStatus[orgId]?.signup_banner
                  ? orgsListWithUserStatus[orgId].signup_banner
                  : "No description available",
                membershipADGroup:
                  orgsListWithUserStatus[orgId]?.membership_ad_group,
                userId: userId,
                platformId: platformId,
                recommended: orgsListWithUserStatus[orgId]?.recommended,
                user_removal_enabled:
                  orgsListWithUserStatus[orgId]?.user_removal_enabled,
              });
            }
          }

          for (const orgId in orgs[1]) {
            if (
              orgsListWithUserStatus[orgId].visible === true || // Modification
              orgsListWithUserStatus[orgId].user_type === "Owner" || // Modification
              orgsListWithUserStatus[orgId].user_type === "Approver" // Modification
            ) {
              orgsListFormatted[1].push({
                s_no: i++,
                id: orgId,
                orgDisplayName: orgsListWithUserStatus[orgId].org_name.replace(
                  /-/g,
                  " "
                ),
                orgName: orgsListWithUserStatus[orgId].org_name,
                status:
                  adRequestStatusMap[
                  orgsListWithUserStatus[orgId].membership_status
                  ],
                action:
                  adRequestStatusMap[
                    orgsListWithUserStatus[orgId].membership_status
                  ] === "Non-Member"
                    ? "Add"
                    : adRequestStatusMap[
                      orgsListWithUserStatus[orgId].membership_status
                    ] === "Failed"
                      ? "Add"
                      : "Delete",
                email: email,
                approvalRequired:
                  orgsListWithUserStatus[orgId]?.approval_required === true
                    ? "Yes"
                    : "No",
                userType: orgsListWithUserStatus[orgId]?.user_type,
                //userType: "Owner",
                signup_banner: orgsListWithUserStatus[orgId]?.signup_banner
                  ? orgsListWithUserStatus[orgId].signup_banner
                  : "No description available",
                membershipADGroup:
                  orgsListWithUserStatus[orgId]?.membership_ad_group,
                userId: userId,
                platformId: platformId,
                recommended: orgsListWithUserStatus[orgId]?.recommended,
                user_removal_enabled:
                  orgsListWithUserStatus[orgId]?.user_removal_enabled,
              });
            }
          }

          return orgsListFormatted;
        } catch (error) {
          console.log(error);
          return error;
        }
      },
      providesTags: [{ type: "User", id: "OrgStatuses" }],
    }),
    // The `getUsersByOrgAndStatus` query is used to retrieve users by organization and status.
    getUsersByOrgAndStatus: builder.query({
      query: ({ orgId, status, platformId, page, perPage, email, sortBy, sortOrder, filterField, filterValue }) => {
        let url = `/users?org_id=${orgId}&membership_status=${status}`;

        if (page && perPage) {
          url += `&page=${page}&per_page=${perPage}`;
        }

        if (email) {
          url += `&email=${email}`;
        }

        if (sortBy) {
          url += `&sort_by=${sortBy}`;
        }

        if (sortOrder) {
          url += `&sort_order=${sortOrder}`;
        }

        if (filterField && filterValue) {
          url += `&${filterField}=${encodeURIComponent(filterValue)}`;
        }

        return { url };
      },
      transformResponse: (
        responseData,
        meta,
        { orgId, platformId, status }
      ) => {
        try {
          const formattedOrgRequests = {
            total: responseData?.total,
            users: [],
          };

          // Ensure responseData.users exists and is iterable
          if (!responseData?.users || !Array.isArray(responseData.users)) {
            return formattedOrgRequests;
          }

          for (const request of responseData.users) {
            const userRequestForOrg =
              request.platforms[platformId].organizations[orgId];
            const allUserOrgs =
              request?.platforms?.[platformId]?.organizations || {};
            const hasJoinedAnyOrg = Object.values(allUserOrgs).some(
              (org) => org?.membership_status === "Joined"
            );

            const date = new Date(
              userRequestForOrg?.status_history[
                userRequestForOrg?.status_history.length - 1
              ]?.changed_date
            );

            const options = {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            };

            const formattedDate = date.toLocaleDateString("en-US", options);

            let fullName = "";
            if (request?.last_name) {
              fullName += `${request?.last_name}, `;
            }
            if (request?.first_name) {
              fullName += `${request?.first_name}`;
            }

            formattedOrgRequests.users.push({
              id: request._id,
              name: fullName,
              firstName: request?.first_name,
              lastName: request?.last_name,
              reason: userRequestForOrg?.reason,
              date: formattedDate,
              costCenter: request.platforms[platformId]?.cost_center_id || "",
              action:
                status === "Pending"
                  ? "approve"
                  : status === "Joined"
                    ? "remove"
                    : status === "Rejected"
                      ? "reset"
                      : status === "Remove-Requested"
                        ? "remove-requested"
                        : status === "Removed"
                          ? "removed"
                          : "",
              status: status.toLowerCase(),
              email: request.email,
              // costCenter: "Cost Center 1",
              // changedBy:
              //   userRequestForOrg?.status_history[
              //     userRequestForOrg?.status_history?.length - 1
              //   ]?.changed_by,
              changedBy: (() => {
                const history = userRequestForOrg?.status_history || [];
                if (!history.length) return "";
                const normalizeStatus = (value) =>
                  String(value || "")
                    .trim()
                    .toLowerCase();

                // History entries store the status before transition; map each
                // table status to the source status that identifies the actor.
                const actorSourceStatusByStatus = {
                  requested: "pending",
                  joined: "pending",
                  rejected: "pending",
                  "remove-requested": "joined",
                  removed: "joined",
                };

                const actorSourceStatus =
                  actorSourceStatusByStatus[normalizeStatus(status)] ||
                  normalizeStatus(status);
                const matchingEntries = history.filter(
                  (entry) =>
                    normalizeStatus(entry.status) === actorSourceStatus
                );
                const entriesToSearch =
                  matchingEntries.length > 0 ? matchingEntries : history;

                const latestEntry = entriesToSearch.reduce((latest, current) =>
                  new Date(current.changed_date) > new Date(latest.changed_date)
                    ? current
                    : latest
                );

                return latestEntry?.changed_by ?? "";
              })(),
              color: generateRandomColor(request?._id),
              rawDate: userRequestForOrg?.status_history?.reduce((max, entry) => {
                const d = new Date(entry.changed_date);
                return d > new Date(max) ? entry.changed_date : max;
              }, userRequestForOrg?.status_history?.[0]?.changed_date),
              isNewUser: !hasJoinedAnyOrg,
              hasUserName: !!request.platforms[platformId]?.user_name,
              hasEndDate: !!request.platforms[platformId]?.end_date,
              // Present only when this user has a Copilot license — its
              // `licensed_organizations` array is what drives the Copilot
              // badge/tooltip in JoinedTab.
              copilotBudget: request?.copilot_budget,
            });
          }

          return formattedOrgRequests;
        } catch (err) {
          console.log(err);
          return err;
        }
      },
      providesTags: (result, error, { status }) => [
        { type: "User", id: `OrgRequests-${status}` }
      ],
    }),
    getCostCenterUsers: builder.query({
      query: () => ({
        url: `/users/costCenters`,
      }),
    }),
    getOrgUserSyncStatus: builder.query({
      query: ({ orgId }) => ({
        url: `/sync/tasks/verify-pending/org/${orgId}`,
      }),
      transformResponse: (responseData) => {
        const statusMap = {
          accepted: "in_progress",
          queued: "in_progress",
          starting: "in_progress",
          in_progress: "in_progress",
          already_running: "in_progress",
          ended: "complete",
          complete: "complete",
          never_run: "never_run",
          error: "error",
        };

        return {
          ...responseData,
          status: statusMap[responseData?.status] || responseData?.status,
        };
      },
      transformErrorResponse: (error) => {
        if (
          error.error_code === 404 &&
          error.error_message === "No sync found for the given organization"
        ) {
          return {
            error_code: 404,
            error_message: "No sync found for the given organization",
          };
        }

        return error;
      },
    }),
    getActiveUserStatus: builder.query({
      query: ({ email }) => ({
        url: `/users/usertracker?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    getAdCheckEmail: builder.query({
      query: (email) => ({
        url: `/users/ad/check-email?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
    }),
    // The `getUserTrackers` query is used to retrieve user trackers with pagination, status, and country filter
    getUserTrackers: builder.query({
      query: ({ page, perPage, country, status, searchParams }) => {
        let url = `/users/usertrackers?page=${page}&per_page=${perPage}`;
        if (country) {
          url += `&country=${encodeURIComponent(country)}`;
        }
        if (status && status !== "all") {
          url += `&status=${encodeURIComponent(status)}`;
        }
        if (searchParams && searchParams.trim()) {
          url += `&searchParams=${encodeURIComponent(searchParams.trim())}`;
        }
        return { url };
      },
      transformResponse: (responseData) => {
        return {
          userTrackers: responseData.user_trackers || [],
          total: responseData.total || 0,
          page: responseData.page || 1,
          perPage: responseData.per_page || 20,
        };
      },
      providesTags: (result, error, arg) => [
        { type: "UserTrackers", id: `${arg.country}-${arg.status}-${arg.page}-${arg.perPage}-${arg.searchParams || ''}` }
      ],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    // The `getUserGitHubMembership` query retrieves a user's GitHub org memberships by email and platform.
    getUserGitHubMembership: builder.query({
      query: ({ email, platformId }) => ({
        url: `/users/user-github-membership?platform_id=${encodeURIComponent(platformId)}&email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
      transformResponse: (responseData) => {
        if (responseData?.organizations?.length > 0) {
          return responseData.organizations[0];
        }
        return null;
      },
      providesTags: [{ type: "User", id: "GitHubMembership" }],
    }),
    // The `getUserBudget` query retrieves a user's AI Credit budget summary:
    // every budget scope that could apply to them (enterprise/cost-center/user),
    // which one is currently effective, any open increase/decrease request,
    // and which orgs they hold a Copilot license in. Budgets/requests change
    // outside this client (admin actions, GitHub usage), so this always
    // refetches on mount/arg-change instead of serving a stale cached value.
    getUserBudget: builder.query({
      query: ({ email, platformId }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}?platform_id=${encodeURIComponent(platformId)}`,
        method: "GET",
      }),
      providesTags: [{ type: "User", id: "Budget" }],
      forceRefetch: () => true,
    }),
    // The `getBudgetRequests` query retrieves a paginated, filterable list of
    // AI Credit budget requests for an org — used by the admin "AIC Budget
    // Requests" table. Defaults mirror the backend's own defaults so the
    // pending-approvals view loads with `status=pending` out of the box.
    // Always refetches — other admins may review requests concurrently.
    getBudgetRequests: builder.query({
      query: ({
        orgId,
        platformId,
        action = "all",
        status = "pending",
        page = 1,
        perPage = 20,
        email = "",
        requestDate = "",
        sortBy = "",
        sortOrder = "asc",
      }) => {
        let url = `/users/copilot/budget-requests?org_id=${encodeURIComponent(orgId)}&platform_id=${encodeURIComponent(platformId)}&action=${encodeURIComponent(action)}&status=${encodeURIComponent(status)}&page=${page}&per_page=${perPage}`;
        if (email) url += `&email=${encodeURIComponent(email)}`;
        if (requestDate) url += `&request_date=${encodeURIComponent(requestDate)}`;
        if (sortBy) url += `&sort_by=${encodeURIComponent(sortBy)}&sort_order=${encodeURIComponent(sortOrder)}`;
        return { url, method: "GET" };
      },
      providesTags: (result, error, { action = "all", status = "pending" }) => [
        { type: "User", id: `BudgetRequests-${action}-${status}` },
      ],
      forceRefetch: () => true,
    }),
    // The `getMyBudgetRequests` query retrieves the signed-in user's own AI
    // Credit budget request history (every org, every status) — used by the
    // "My Requests" tab in UlbRequestModal. Always refetches — an admin may
    // review a request while this tab is open.
    getMyBudgetRequests: builder.query({
      query: ({ email, platformId, status = "all", page = 1, perPage = 20 }) => ({
        url: `/users/copilot/budget/${encodeURIComponent(email)}/requests?platform_id=${encodeURIComponent(platformId)}&status=${encodeURIComponent(status)}&page=${page}&per_page=${perPage}`,
        method: "GET",
      }),
      providesTags: (result, error, { status = "all" }) => [
        { type: "User", id: `MyBudgetRequests-${status}` },
      ],
      forceRefetch: () => true,
    }),
  }),
});

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetUserByEmailQuery,
  useGetUserWithOrgMembershipStatusesQuery,
  useLazyGetUserWithOrgMembershipStatusesQuery,
  useGetUsersByOrgAndStatusQuery,
  useLazyGetUsersByOrgAndStatusQuery,
  useGetCostCenterUsersQuery,
  useGetOrgUserSyncStatusQuery,
  useLazyGetActiveUserStatusQuery,
  useGetActiveUserStatusQuery,
  useGetAdCheckEmailQuery,
  useLazyGetAdCheckEmailQuery,
  useGetUserTrackersQuery,
  useLazyGetUserTrackersQuery,
  useGetUserGitHubMembershipQuery,
  useLazyGetUserGitHubMembershipQuery,
  useGetUserBudgetQuery,
  useLazyGetUserBudgetQuery,
  useGetBudgetRequestsQuery,
  useGetMyBudgetRequestsQuery,
} = userServiceApiQueries;
